/**
 * Fee rules and per-zone travel rules (admin doc 11 §3.5–§3.6) — the two computed charges, as
 * configuration. Split from `Pricing.tsx`, which keeps the version workbench.
 *
 * **A rule a quote has priced through is frozen.** The server stamps `first_used_at` the first
 * time a quote resolves through a rule, and from then on only `is_active`, `active_to` (and a fee
 * rule's `note`) may change. The form for a frozen rule offers only those fields and says to
 * create a successor instead. The server is still the authority: a rule on a published sheet is
 * frozen too, and its `409 rule_in_use` is shown verbatim.
 *
 * **A rate is a fraction, typed as a percent.** `0.1500` is fifteen per cent; the operator types
 * `15` and the server receives `"0.1500"`. The conversion reads digits, never floats, for the
 * same reason `parsePaise` does.
 *
 * **Dates are IST days.** A window typed as `2026-11-01` starts at midnight in Kolkata, which is
 * what an operator in Patna means by "from the first".
 *
 * **The worked travel sample is the server's figure.** Under the form, a sample distance is priced
 * by `GET /v1/admin/travel-charge-rules/sample`, which runs the quote engine's own resolver over
 * the draft amounts; this screen only words the answer. Built from the doc by the owner's ruling
 * of 2026-10-26 (the design draws no sample). The precedence-tie banner has nothing to show: quotes
 * price fee lines by their explicit `fee_rule_id`, so no runtime tie exists (doc 11 §3.5).
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Money, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { ApiError, errorMessage } from '../lib/api/client';
import { formatInr, parsePaise } from '../lib/money';
import { useCities, useTravelZones } from '../lib/api/hooks/geo';
import {
  useCreateFeeRule,
  useCreateTravelRule,
  useFeeRules,
  usePatchFeeRule,
  usePatchTravelRule,
  useTravelRules,
  useTravelSample,
  type FeeRuleCreateIn,
  type FeeRulePatchIn,
  type FeeRuleRow,
  type TravelRulePatchIn,
  type TravelRuleRow,
  type TravelSample,
  type TravelSampleQuery,
} from '../lib/api/hooks/pricing';

export const FEE_APPLIES = ['platform_fee', 'convenience_fee', 'cancellation_fee', 'reschedule_fee'] as const;
export const RATE_BASES = ['pandit_fee', 'items_subtotal', 'grand_total'] as const;
const PLATFORM_WIDE = 'Platform-wide';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const FROZEN_HINT =
  'A rule a quote has resolved through is frozen — create a successor rule instead. Only switching it off, ending it' +
  ' or its note can change.';

/** `15` → `"0.1500"`, `7.5` → `"0.0750"`. Two percent decimals is the column's four. Null on anything else. */
export function percentToFraction(input: string): string | null {
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(input.trim());
  if (!m) return null;
  const basis = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  if (basis > 10000) return null;
  return `${Math.floor(basis / 10000)}.${String(basis % 10000).padStart(4, '0')}`;
}

/** `"0.1500"` → `"15"`, `"0.0750"` → `"7.5"`. The inverse, for display and the edit form. */
export function fractionToPercent(fraction: string): string {
  const [whole = '0', frac = ''] = fraction.split('.');
  const basis = Number(whole) * 10000 + Number(frac.padEnd(4, '0').slice(0, 4));
  const rest = String(basis % 100).padStart(2, '0').replace(/0+$/, '');
  return rest ? `${Math.floor(basis / 100)}.${rest}` : String(Math.floor(basis / 100));
}

/** A server timestamp as the IST calendar day. A naive one is UTC, which is how the API stores it. */
export function istDay(iso: string): string {
  const zoned = /(Z|[+-]\d{2}:\d{2})$/.test(iso) ? iso : `${iso}Z`;
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(zoned));
}

const istMidnight = (day: string) => `${day}T00:00:00+05:30`;
const rupees = (paise: number | null | undefined) =>
  paise === null || paise === undefined ? '' : String(paise / 100);
const dateError = (day: string, required = false) =>
  (day === '' && !required) || ISO_DATE.test(day) ? undefined : 'A date as YYYY-MM-DD';
/** An optional amount: empty is null, anything unparseable is `undefined` (invalid). */
const optionalPaise = (raw: string) => (raw.trim() === '' ? null : (parsePaise(raw) ?? undefined));

function Actions({
  saving,
  canSave,
  onSave,
  onCancel,
}: {
  saving: boolean;
  canSave: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { scope } = useConsole();
  return (
    <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={!canSave || saving} onClick={onSave}>
        Save rule
      </ScopeAction>
      <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </ScopeAction>
    </div>
  );
}

/* ── Fee rules ──────────────────────────────────────────────────────────────────────────── */

function FeeRuleForm({ rule, onDone }: { rule?: FeeRuleRow; onDone: () => void }) {
  const { toast } = useConsole();
  const cities = useCities({ page: 1, page_size: 200 });
  const create = useCreateFeeRule();
  const patch = usePatchFeeRule();
  const frozen = rule?.first_used_at !== null && rule?.first_used_at !== undefined;
  const [code, setCode] = useState('');
  const [applies, setApplies] = useState<string>('platform_fee');
  const [mode, setMode] = useState<string>(rule?.rate ? 'rate' : 'flat');
  const [flat, setFlat] = useState(rupees(rule?.flat_paise));
  const [percent, setPercent] = useState(rule?.rate ? fractionToPercent(rule.rate) : '');
  const [base, setBase] = useState<string>(rule?.rate_base ?? 'pandit_fee');
  const [min, setMin] = useState(rupees(rule?.min_paise));
  const [cap, setCap] = useState(rupees(rule?.cap_paise));
  const [city, setCity] = useState(PLATFORM_WIDE);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(rule?.active_to ? istDay(rule.active_to) : '');
  const [note, setNote] = useState(rule?.note ?? '');
  const [active, setActive] = useState(rule?.is_active ?? true);

  const flatPaise = parsePaise(flat);
  const fraction = percentToFraction(percent);
  const minPaise = optionalPaise(min);
  const capPaise = optionalPaise(cap);
  const cityRows = cities.data?.items ?? [];
  const charge =
    mode === 'flat'
      ? { flat_paise: flatPaise, rate: null, rate_base: null }
      : { flat_paise: null, rate: fraction, rate_base: base };
  const chargeOk = frozen || (mode === 'flat' ? flatPaise !== null && flatPaise >= 0 : fraction !== null);
  const valid =
    chargeOk &&
    minPaise !== undefined &&
    capPaise !== undefined &&
    dateError(to) === undefined &&
    (rule !== undefined || (code.trim().length >= 2 && dateError(from, true) === undefined));
  const mutation = rule ? patch : create;

  const save = () => {
    const done = {
      onSuccess: () => {
        toast({ tone: 'success', message: rule ? `Saved ${rule.code}` : `Created ${code.trim()}` });
        onDone();
      },
    };
    const activeTo = to === '' ? null : istMidnight(to);
    if (!rule) {
      const body: FeeRuleCreateIn = {
        code: code.trim(),
        applies_to: applies,
        ...charge,
        min_paise: minPaise,
        cap_paise: capPaise,
        city_id: cityRows.find((c) => c.name === city)?.id ?? null,
        active_from: istMidnight(from),
        active_to: activeTo,
        note: note.trim() || null,
      };
      create.mutate(body, done);
      return;
    }
    // Only what changed: a rule in use refuses an amount field even when its value is unchanged.
    const candidate: FeeRulePatchIn = frozen
      ? {}
      : { ...charge, min_paise: minPaise, cap_paise: capPaise };
    Object.assign(candidate, { is_active: active, active_to: activeTo, note: note.trim() || null });
    const current: Record<string, unknown> = {
      ...rule,
      active_to: rule.active_to ? istMidnight(istDay(rule.active_to)) : null,
      note: rule.note ?? null,
    };
    const changes = Object.fromEntries(
      Object.entries(candidate).filter(([k, v]) => (current[k] ?? null) !== v),
    ) as FeeRulePatchIn;
    patch.mutate({ ruleId: rule.id, patch: changes }, done);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-md)' }}>
      {frozen && <StatusChip tone="warn" label={FROZEN_HINT} />}
      {!rule && (
        <>
          <Input label="Code" value={code} onChange={setCode} helper="A slug ops will recognise, e.g. platform-fee-patna" />
          <Select label="Applies to" options={FEE_APPLIES} value={applies} onChange={setApplies} />
          <Select
            label="Scope"
            options={[PLATFORM_WIDE, ...cityRows.map((c) => c.name)]}
            value={city}
            onChange={setCity}
          />
        </>
      )}
      {!frozen && (
        <>
          <Select label="Charge" options={['flat', 'rate']} value={mode} onChange={setMode} />
          {mode === 'flat' ? (
            <Input
              label="Flat amount"
              prefix="₹"
              inputMode="decimal"
              value={flat}
              onChange={setFlat}
              error={flat !== '' && flatPaise === null ? 'An amount in rupees, e.g. 199 or 199.50' : undefined}
            />
          ) : (
            <>
              <Input
                label="Rate (%)"
                inputMode="decimal"
                value={percent}
                onChange={setPercent}
                helper={fraction ? `Stored as ${fraction}` : 'Up to two decimals, e.g. 15 or 7.5'}
                error={percent !== '' && fraction === null ? 'A percent from 0 to 100' : undefined}
              />
              <Select label="Of" options={RATE_BASES} value={base} onChange={setBase} />
            </>
          )}
          <Input label="Minimum" prefix="₹" inputMode="decimal" value={min} onChange={setMin} helper="Optional clamp"
            error={minPaise === undefined ? 'An amount in rupees' : undefined} />
          <Input label="Cap" prefix="₹" inputMode="decimal" value={cap} onChange={setCap} helper="Optional clamp"
            error={capPaise === undefined ? 'An amount in rupees' : undefined} />
        </>
      )}
      {!rule && (
        <Input label="Active from" value={from} onChange={setFrom} placeholder="YYYY-MM-DD"
          helper="Required — the day it starts, midnight IST" error={from === '' ? undefined : dateError(from, true)} />
      )}
      <Input label="Active to" value={to} onChange={setTo} placeholder="Open-ended" helper="YYYY-MM-DD, optional"
        error={dateError(to)} />
      <Input label="Note" value={note} onChange={setNote} helper="Why this rule exists" />
      {rule && <Toggle label="Active" on={active} onChange={setActive} />}
      {mutation.isError && (
        <ErrorState inline title="The server refused this rule" cause={errorMessage(mutation.error)} />
      )}
      <Actions saving={mutation.isPending} canSave={valid} onSave={save} onCancel={onDone} />
    </div>
  );
}

function feeCharge(r: FeeRuleRow) {
  if (r.flat_paise !== null && r.flat_paise !== undefined) return <Money paise={r.flat_paise} showPaise />;
  return (
    <span className="mono-num">
      {/* Flat XOR rate: no flat amount means the server sent a rate. */}
      {fractionToPercent(r.rate!)}% × {r.rate_base}
    </span>
  );
}

export function FeeRules() {
  const { scope } = useConsole();
  const feeRules = useFeeRules();
  const cities = useCities({ page: 1, page_size: 200 });
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const cityName = (id: number | null | undefined) =>
    id === null || id === undefined ? 'platform' : (cities.data?.items.find((c) => c.id === id)?.name ?? `city ${id}`);
  const editingRule = feeRules.data?.items.find((r) => r.id === editing);

  return (
    <Section
      title="Fee rules"
      note="Flat or a rate, never both · city beats platform, then the latest start wins"
      pad={false}
      actions={
        editing === null && (
          <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setEditing('new')}>
            New fee rule
          </ScopeAction>
        )
      }
    >
      {editing === 'new' && <FeeRuleForm onDone={() => setEditing(null)} />}
      {editingRule && <FeeRuleForm key={editingRule.id} rule={editingRule} onDone={() => setEditing(null)} />}
      {feeRules.isPending && <LoadingState message="Reading fee rules…" />}
      {feeRules.isError && <ErrorState title="Could not read fee rules" cause={errorMessage(feeRules.error)} />}
      {feeRules.isSuccess && feeRules.data.items.length === 0 && <EmptyState icon="tag" title="No fee rule yet" inline />}
      {feeRules.data && feeRules.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'code', label: 'Code', mono: true },
            { key: 'applies', label: 'Applies to' },
            { key: 'charge', label: 'Charge', align: 'right', mono: true },
            { key: 'scope', label: 'Scope' },
            { key: 'window', label: 'Window', mono: true },
            { key: 'state', label: 'State' },
            { key: 'edit', label: '' },
          ]}
          rows={feeRules.data.items.map((r) => ({
            id: r.id,
            code: r.code,
            applies: r.applies_to,
            charge: feeCharge(r),
            scope: cityName(r.city_id),
            window: `${istDay(r.active_from)} → ${r.active_to ? istDay(r.active_to) : 'open'}`,
            state: (
              <span style={{ display: 'flex', gap: 4 }}>
                <StatusChip tone={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'active' : 'off'} />
                {r.first_used_at && <StatusChip tone="warn" label="in use" />}
              </span>
            ),
            edit: (
              <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={() => setEditing(r.id)}>
                Edit
              </ScopeAction>
            ),
          }))}
        />
      )}
    </Section>
  );
}

/* ── Travel rules ───────────────────────────────────────────────────────────────────────── */

const DISTANCE = /^\d{1,3}(\.\d{1,2})?$/;

/** "11.2 km → base ₹100.00 + ₹15.00 × 7 km = ₹205.00", every term from the server. */
export function sampleSentence(s: TravelSample): string {
  const money = (paise: number) => formatInr(paise, { paise: true });
  const terms =
    s.chargeable_km === 0
      ? `within the free ${s.free_within_km} km, base ${money(s.base_paise)}`
      : `base ${money(s.base_paise)} + ${money(s.per_km_paise)} × ${s.chargeable_km} km beyond ${s.free_within_km} km`;
  const cap = s.capped ? ` (${money(s.uncapped_paise)}, capped)` : '';
  return `${s.distance_km} km → ${terms}${cap} = ${money(s.total_paise)}`;
}

type TravelAmounts = Pick<TravelSampleQuery, 'free_within_km' | 'base_paise' | 'per_km_paise' | 'cap_paise'>;

function WorkedSample({ amounts }: { amounts: TravelAmounts | undefined }) {
  const [distance, setDistance] = useState('11.2');
  const ok = DISTANCE.test(distance);
  const sample = useTravelSample(amounts && ok ? { distance_km: distance, ...amounts } : undefined);
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
      <Input label="Sample distance (km)" inputMode="decimal" value={distance} onChange={setDistance}
        error={ok ? undefined : 'Kilometres, up to two decimals'} />
      {amounts === undefined && <span>Fix the amounts to see a worked sample.</span>}
      {sample.isSuccess && <span data-testid="travel-sample">{sampleSentence(sample.data)}</span>}
      {sample.isError && <ErrorState inline title="No worked sample" cause={errorMessage(sample.error)} />}
    </div>
  );
}

function TravelRuleForm({ zoneId, rule, onDone }: { zoneId: number; rule?: TravelRuleRow; onDone: () => void }) {
  const { toast } = useConsole();
  const create = useCreateTravelRule();
  const patch = usePatchTravelRule();
  const frozen = rule?.first_used_at !== null && rule?.first_used_at !== undefined;
  const [freeKm, setFreeKm] = useState(String(rule?.free_within_km ?? 0));
  const [base, setBase] = useState(rupees(rule?.base_paise ?? 0));
  const [perKm, setPerKm] = useState(rupees(rule?.per_km_paise ?? 0));
  const [cap, setCap] = useState(rupees(rule?.cap_paise));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(rule?.active_to ? istDay(rule.active_to) : '');
  const [active, setActive] = useState(rule?.is_active ?? true);
  const mutation = rule ? patch : create;
  const overlap = mutation.error instanceof ApiError && mutation.error.code === 'rule_window_overlap';

  const basePaise = parsePaise(base);
  const perKmPaise = parsePaise(perKm);
  const capPaise = optionalPaise(cap);
  const amountsOk =
    frozen || (/^\d{1,3}$/.test(freeKm) && basePaise !== null && perKmPaise !== null && capPaise !== undefined);
  const valid = amountsOk && dateError(to) === undefined && (rule !== undefined || dateError(from, true) === undefined);

  const save = () => {
    const done = {
      onSuccess: () => {
        toast({ tone: 'success', message: rule ? 'Travel rule saved' : 'Travel rule added' });
        onDone();
      },
    };
    const activeTo = to === '' ? null : istMidnight(to);
    const amounts = { free_within_km: Number(freeKm), base_paise: basePaise!, per_km_paise: perKmPaise!, cap_paise: capPaise };
    if (!rule) {
      create.mutate({ zoneId, body: { ...amounts, active_from: istMidnight(from), active_to: activeTo } }, done);
      return;
    }
    const candidate: TravelRulePatchIn = { ...(frozen ? {} : amounts), is_active: active, active_to: activeTo };
    const current: Record<string, unknown> = {
      ...rule,
      active_to: rule.active_to ? istMidnight(istDay(rule.active_to)) : null,
    };
    const changes = Object.fromEntries(
      Object.entries(candidate).filter(([k, v]) => (current[k] ?? null) !== v),
    ) as TravelRulePatchIn;
    patch.mutate({ ruleId: rule.id, patch: changes }, done);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-md)' }}>
      {frozen && <StatusChip tone="warn" label="This rule has priced quotes — add a new rule for new amounts." />}
      {!frozen && (
        <>
          <Input label="Free within (km)" inputMode="numeric" value={freeKm} onChange={setFreeKm}
            error={/^\d{1,3}$/.test(freeKm) ? undefined : 'Whole kilometres'} />
          <Input label="Base" prefix="₹" inputMode="decimal" value={base} onChange={setBase}
            error={basePaise === null ? 'An amount in rupees' : undefined} />
          <Input label="Per km" prefix="₹" inputMode="decimal" value={perKm} onChange={setPerKm}
            error={perKmPaise === null ? 'An amount in rupees' : undefined} />
          <Input label="Cap" prefix="₹" inputMode="decimal" value={cap} onChange={setCap} helper="Optional"
            error={capPaise === undefined ? 'An amount in rupees' : undefined} />
        </>
      )}
      {!rule && (
        <Input label="Active from" value={from} onChange={setFrom} placeholder="YYYY-MM-DD" helper="Required — the day it starts, midnight IST"
          error={overlap ? 'Overlaps another rule on this zone' : from === '' ? undefined : dateError(from, true)} />
      )}
      <Input label="Active to" value={to} onChange={setTo} placeholder="Open-ended" helper="YYYY-MM-DD, optional"
        error={overlap && rule ? 'Overlaps another rule on this zone' : dateError(to)} />
      <WorkedSample
        amounts={
          frozen
            ? { free_within_km: rule!.free_within_km, base_paise: rule!.base_paise, per_km_paise: rule!.per_km_paise, cap_paise: rule!.cap_paise ?? null }
            : amountsOk
              ? { free_within_km: Number(freeKm), base_paise: basePaise!, per_km_paise: perKmPaise!, cap_paise: capPaise ?? null }
              : undefined
        }
      />
      {rule && <Toggle label="Active" on={active} onChange={setActive} />}
      {mutation.isError && (
        <ErrorState inline title="The server refused this rule" cause={errorMessage(mutation.error)} />
      )}
      <Actions saving={mutation.isPending} canSave={valid} onSave={save} onCancel={onDone} />
    </div>
  );
}

export function TravelRules() {
  const { scope } = useConsole();
  const cities = useCities({ page: 1, page_size: 200 });
  const [cityName, setCityName] = useState('');
  const cityId = cities.data?.items.find((c) => c.name === cityName)?.id;
  const zones = useTravelZones(cityId);
  const [zoneLabel, setZoneLabel] = useState('');
  const zone = zones.data?.find((z) => z.label === zoneLabel);
  const rules = useTravelRules(zone?.id);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const editingRule = rules.data?.items.find((r) => r.id === editing);

  return (
    <Section
      title="Travel rules"
      note="Per travel zone · base + per km beyond the free radius, capped"
      pad={false}
      actions={
        zone &&
        editing === null && (
          <ScopeAction scope={scope} need="catalogue_content" size="sm" icon="plus" onClick={() => setEditing('new')}>
            New travel rule
          </ScopeAction>
        )
      }
    >
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', padding: 'var(--sp-md)' }}>
        <Select
          label="Travel city"
          placeholder="Choose a city"
          options={(cities.data?.items ?? []).map((c) => c.name)}
          value={cityName}
          onChange={(next) => {
            setCityName(next);
            setZoneLabel('');
            setEditing(null);
          }}
        />
        <Select
          label="Travel zone"
          placeholder="Choose a zone"
          options={(zones.data ?? []).map((z) => z.label)}
          value={zoneLabel}
          disabled={cityId === undefined}
          onChange={(next) => {
            setZoneLabel(next);
            setEditing(null);
          }}
        />
      </div>
      {zone && (
        <div style={{ padding: '0 var(--sp-md)', color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
          {zone.min_km}–{zone.max_km} km · a zone's band is frozen once a quote has used it
        </div>
      )}
      {zones.isError && <ErrorState title="Could not read zones" cause={errorMessage(zones.error)} />}
      {zone && editing === 'new' && <TravelRuleForm zoneId={zone.id} onDone={() => setEditing(null)} />}
      {zone && editingRule && (
        <TravelRuleForm key={editingRule.id} zoneId={zone.id} rule={editingRule} onDone={() => setEditing(null)} />
      )}
      {rules.isError && <ErrorState title="Could not read travel rules" cause={errorMessage(rules.error)} />}
      {rules.isSuccess && rules.data.items.length === 0 && <EmptyState icon="map-pin" title="No travel rule on this zone" inline />}
      {rules.data && rules.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'free', label: 'Free km', align: 'right', mono: true },
            { key: 'base', label: 'Base', align: 'right', mono: true },
            { key: 'perKm', label: 'Per km', align: 'right', mono: true },
            { key: 'cap', label: 'Cap', align: 'right', mono: true },
            { key: 'window', label: 'Window', mono: true },
            { key: 'state', label: 'State' },
            { key: 'edit', label: '' },
          ]}
          rows={rules.data.items.map((r) => ({
            id: r.id,
            free: r.free_within_km,
            base: <Money paise={r.base_paise} showPaise />,
            perKm: <Money paise={r.per_km_paise} showPaise />,
            cap: r.cap_paise === null || r.cap_paise === undefined ? '—' : <Money paise={r.cap_paise} showPaise />,
            window: `${istDay(r.active_from)} → ${r.active_to ? istDay(r.active_to) : 'open'}`,
            state: (
              <span style={{ display: 'flex', gap: 4 }}>
                <StatusChip tone={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'active' : 'off'} />
                {r.first_used_at && <StatusChip tone="warn" label="in use" />}
              </span>
            ),
            edit: (
              <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={() => setEditing(r.id)}>
                Edit
              </ScopeAction>
            ),
          }))}
        />
      )}
    </Section>
  );
}
